import type { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro";
import type { TestFilterOptions } from "./MetricsTable.types";

/** Passed to `useSearchParamState` for asset-tree URL params (`replaceState`). */
export const SEARCH_PARAM_OPTS = { replace: true } as const;

export const HEADER_FILTER_KEYS = [
  "is_subscribed",
  "metric_id",
  "test_id",
  "task_name",
  "metric_group_type",
  "asset_type",
  "readable_name",
  "asset_name",
  "app_name",
] as const;

export type HeaderFilterKeysTuple = typeof HEADER_FILTER_KEYS;
export type HeaderFilterKey = HeaderFilterKeysTuple[number];

export const TEST_FILTER_ID = "tests_from_header";

/** Chip labels for the header (per-column) filters — see `ActiveFilterChips`. */
export const HEADER_FILTER_LABELS: { [k in HeaderFilterKey]: string } = {
  is_subscribed: "Is subscribed",
  metric_id: "MID",
  test_id: "Test ID",
  task_name: "Task name",
  metric_group_type: "Metric group",
  asset_type: "Asset group",
  readable_name: "Metric name",
  asset_name: "Asset name",
  app_name: "Pipeline",
};

/** Single source for the tests & alerts filter wording (dropdown + chip). */
export const TEST_FILTER_LABELS: { [K in TestFilterOptions]: string } = {
  "": "None",
  "has-test-suggestions": "Has test suggestions",
  "has-alerts": "Has incidents",
  "has-alerts-notifications": "Notifies on test failure",
  "has-tests": "Has tests",
  "has-breaking-tests": "Has breaking tests",
  "no-tests": "No active tests",
  "is-subscribed": "Is subscribed",
};

// IMPORTANT: the quick filter will also consider cell values from hidden columns (MID).
//
// Frozen (incl. `items`): this object is the seed for `useReadFilters` and is intended to
// be fed straight into `produce(...)` (immer) — never mutated in place. Freezing makes any
// accidental `.items.push(...)` / direct property assignment throw in strict mode instead
// of silently leaking shared state across all subscribers (and across renders). The
// `unknown` cast bridges the `readonly` freeze types with `GridFilterModel`'s mutable
// surface; immer copy-on-write guarantees we never actually mutate this object.
export const EMPTY_FILTER_MODEL = Object.freeze({
  items: Object.freeze([] as GridFilterItem[]),
  quickFilterExcludeHiddenColumns: false,
}) as unknown as GridFilterModel;

export const TEST_FILTER_ITEMS: { [K in TestFilterOptions]: GridFilterItem } = {
  "": { id: TEST_FILTER_ID, field: "", operator: "" },
  "has-tests": {
    id: TEST_FILTER_ID,
    field: "tests_count",
    value: 0,
    operator: ">",
  },
  "has-alerts-notifications": {
    id: TEST_FILTER_ID,
    field: "should_notify",
    value: "true",
    operator: "is",
  },
  "no-tests": {
    id: TEST_FILTER_ID,
    field: "tests_count",
    value: 0,
    operator: "=",
  },
  "has-alerts": {
    id: TEST_FILTER_ID,
    field: "alerts_count",
    value: 0,
    operator: ">",
  },
  "has-test-suggestions": {
    id: TEST_FILTER_ID,
    field: "has_suggested_test",
    value: "true",
    operator: "is",
  },
  "has-breaking-tests": {
    id: TEST_FILTER_ID,
    field: "should_break",
    value: "true",
    operator: "is",
  },
  "is-subscribed": {
    id: TEST_FILTER_ID,
    field: "is_subscribed",
    value: "true",
    operator: "is",
  },
};

// TODO: replace the below usage with palette colors (need to create a palette for line-charts or use one of nivo's palettes)
export const SPARKLINE_COLORS = [
  "#2563eb",
  "#16a34a",
  "#dc2626",
  "#5e35b1",
  "#ea580c",
  "#0d9488",
  "#0891b2",
  "#db2777",
  "#d97706",
  "#65a30d",
  "#64748b",
];

export const CLASS_NAMES = {
  HIDE_CHECKBOX: "hide-row-checkbox",
  CRITICAL: "type-error",
  WARNING: "type-warning",
};

export const COLUMN_HEADER_HEIGHT = 70;
export const ROW_HEIGHT = 46;

export const LOCALE_TEXT = {
  toolbarExport: "EXPORT",
  toolbarDensity: "",
  toolbarFilters: "FILTERS",
  toolbarColumns: "",
};
